/**
 * @class
 */
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import Base from '@netx/core/lib/netx-collection';
import {randomChars} from '@netx/core/utils/randomChars';
import Model from '../models/CMSAction';
import {links as cmsLinks} from '../properties/cms';

export default class CMSActions extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} methods - The methods available on this model
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getActionsForPortal(opts) {
				return ['getActionsForPortal', this.portalId];
			},
			/**
			 * @method
			 */
			fetchAll: ['getAllActions'],
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				if (!this.portalId) {
					console.error('undefined portalId');
				}
				return ['getActionsForPortal', this.portalId];
			},
		};
	}

	/**
	 * Parsers for this model
	 * @type {object}
	 */
	get parsers() {
		return {
			/** @method */
			getActionsForPortal(actions) {
				//Netx.Actions.Register(actions, {silent: true});
				this.set(actions, {parse: true});
				this.trigger('sync', this);
			},
			/** @method */
			fetchAll(actions) {
				//Netx.Actions.Register(actions, {silent: true});
				this.set(actions, {parse: true});
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * id of a portal -- for getActionsForPortal()
		 * @property {string}
		 */
		this.portalId = opts.portalId;

		// Hey, listen!
		this.listenTo(dispatcher, 'cms:action:new:success', (form, action) => {
			if (action.get('portalId') !== this.portalId) return;
			this.add(action, {merge: true});
		}).listenTo(dispatcher, 'cms:action:delete:confirmed', (action) => {
			this.destroyAction(action);
		});

		if (opts.pages !== false) {
			this.listenTo(
				dispatcher,
				'cms:page:save:confirmed',
				(form, pageModel) => {
					if (pageModel.get('portalId') !== this.portalId) return;

					// have we fetched yet?
					if (this.length === 0) {
						this.fetch().then(() => {
							this.manageActionFromPageSave(form, pageModel);
						});
					} else {
						this.manageActionFromPageSave(form, pageModel);
					}
				},
			);
		}
	}

	manageActionFromPageSave(form, pageModel) {
		const action = this.find({link: '#page/' + pageModel.id});
		if (!action) {
			this.createActionForPage(pageModel);
		} else if (action.get('label') !== pageModel.get('title')) {
			action.set({label: pageModel.get('title')});
			action.save();
			dispatcher.trigger('cms:action:edit:success', form, action);
		}
	}

	/**
	 * Takes a page and creates an action
	 * @method
	 * @param {object} page - CMSPage model
	 */
	createActionForPage(page) {
		const typePage = cmsLinks.types.page;
		const newAction = new this.model({
			portalId: page.get('portalId'),
			event: 'cms:action:' + page.get('portalId') + ':' + randomChars(10),
			label: page.get('title') || i18n.t('i18n.untitledPage'),
			type: typePage,
			userId: userProfile.id,
		});

		const link = newAction.makeLink(typePage, page.id);

		if (this.findWhere({link: link})) {
			return new $.Deferred().resolve(page);
		}

		newAction.set({link: link});
		page.action = newAction;

		return newAction.save().then((data) => {
			// add to collection after it has an id
			this.add(newAction);
			return page;
		});
	}

	/**
	 * @method
	 * @param {object} action Action
	 */
	destroyAction(action) {
		let actionOb = action.toJSON();
		if (action && action.get('portalId') === this.portalId) {
			action
				.destroy({wait: true})
				.then(() => {
					this.remove(action);
					dispatcher.trigger('cms:action:delete:success', actionOb);
				})
				.catch((err) => {
					dispatcher.trigger('cms:action:delete:error', err, action);
				});
		}
	}

	/** @method */
	fetchPortal(id) {
		id = parseInt(id);
		this.portalId = id;
		if (id) {
			return this.getActionsForPortal.apply(this, _.rest(arguments));
		}
	}

	/** @override */
	unpackAttrs(goo) {
		const unpacked = super.unpackAttrs(goo);
		// sanitize the user provided information
		// TODO: need to make a utility for this so we can use this as it is sure to come up again
		// like sanitizeUserData(obj: object, keys: string[]) => Sanitized object
		// or we santize things on the way out to the server
		// or we make the server sanitize goo
		// or ...
		const sanitized = unpacked
			? {
					...unpacked,
					event: _.escape(unpacked.event),
					label: _.escape(unpacked.label),
			  }
			: unpacked;
		return sanitized;
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSActions';
	}
}
